import { createMemo, Show } from 'solid-js';
import { Button } from '@bz/ui';
import Table from '@bz/wallet/src/components/ui/Table';
import { SuspenseImage } from '@bz/ui';
import { walletSettings } from '@bz/common';
import { Tooltip } from '@bz/ui/src/components/Tooltips';

import { getCurrencyIcon } from '~/utils';

type LimitType = 'wager' | 'loss';
type LimitPeriod = '1day' | '7days' | '30days';

type LimitRecord = ApiSchemas['LimitTypeVO'];

function periodLabel(p: string) {
  if (p === 'day') return t('1 day');
  if (p === 'week') return t('7 days');
  return t('1 month');
}

function typeLabel(t_: string) {
  return t_ === 'wager' ? t('Wager Limit') : t('Loss Limit');
}

function timeRemaining(endTime: number, currentTime: number): string {
  const diff = Math.max(0, endTime - currentTime);
  const totalMins = Math.floor(diff / 60000);
  const days = Math.floor(totalMins / 1440);
  const hours = Math.floor((totalMins % 1440) / 60);
  const mins = totalMins % 60;
  if (days > 0) return `${days} ${t('days')}`;
  if (hours > 0) return `${hours} ${t('hours')}`;
  return `${mins} ${t('mins')}`;
}

function canRemove(currentTime: number, createTime: number, limitHour: number): boolean {
  return currentTime - createTime >= limitHour * 60 * 60 * 1000;
}

function AmountCell(props: { value: number; currency?: string }) {
  const currency = () => props.currency || walletSettings.showCurrency || 'USD';
  return (
    <span class="flex items-center gap-0.5 justify-end font-semibold text-sm text-neutral-main1 leading-[1.2]">
      ${props.value.toFixed(2)}
      <SuspenseImage class="size-5 ml-0.5" src={getCurrencyIcon(currency(), true)} alt={currency()} />
    </span>
  );
}

export const route = {
  info: {
    login: true,
  },
} satisfies RouteDefinition;
export default function LimitSettings() {
  const limits = createAsync(() => get.getUserInsuranceGetLimits().catch());
  const hasLimits = createMemo(() => !!limits?.()?.length);
  const hasWager = createMemo(() => limits()?.some((l) => l.limitType === 'wager'));
  const hasLoss = createMemo(() => limits()?.some((l) => l.limitType === 'loss'));
  const canAddMore = createMemo(() => !hasWager() || !hasLoss());

  const handleRemove = (limit: LimitRecord) => {
    get
      .getUserInsuranceRemoveLimit({ limitType: limit.limitType })
      .then(() => {
        revalidate([get.getUserInsuranceGetLimits.key, get.getUserInsuranceGetInsuranceStatus.key]);
      })
      .catch(toast);
  };

  const columns = Table.createColumns<LimitRecord & Record<string, unknown>>([
    {
      key: 'limitType',
      title: <span class="font-semibold text-[14px] text-neutral-main3 leading-[1.2]">{t('Type')}</span>,
      align: 'left',
      render: (r) => (
        <span class="font-semibold text-sm text-neutral-main2 leading-[1.2]">{typeLabel(r.limitType)}</span>
      ),
    },
    {
      key: 'dayType',
      title: <span class="font-semibold text-[14px] text-neutral-main3 leading-[1.2]">{t('Period')}</span>,
      align: 'left',
      render: (r) => (
        <span class="font-semibold text-sm text-neutral-main2 leading-[1.2]">{periodLabel(r.dayType)}</span>
      ),
    },
    {
      key: 'endTime',
      title: <span class="font-semibold text-[14px] text-neutral-main3 leading-[1.2]">{t('Time Remaining')}</span>,
      align: 'left',
      render: (r) => (
        <span class="font-semibold text-sm text-neutral-main2 leading-[1.2]">
          {timeRemaining(r.endTime, r.currentTime)}
        </span>
      ),
    },
    {
      key: 'limitAmount',
      title: (
        <span class="font-semibold text-[14px] text-neutral-main3 leading-[1.2] w-full text-right block">
          {t('Limit')}
        </span>
      ),
      align: 'right',
      render: (r) => <AmountCell value={r.limitAmount} currency={r.currencyCode} />,
    },
    {
      key: 'progressAmount',
      title: (
        <span class="font-semibold text-[14px] text-neutral-main3 leading-[1.2] w-full text-right flex items-center justify-end gap-1">
          {t('Progress')}
          <Tooltip content={t('There may be a certain delay in betting settlement.')} position="top">
            <IconCheckQuestion class="size-4 ml-[.12rem] cursor-pointer" />
          </Tooltip>
        </span>
      ),
      align: 'right',
      render: (r) => <AmountCell value={r.progressAmount} currency={r.currencyCode} />,
    },
    {
      key: 'limitType',
      title: (
        <span class="font-semibold text-[14px] text-neutral-main3 leading-[1.2] w-full text-right flex items-center justify-end gap-1">
          {t('Action')}
          <Tooltip
            content={t('Any changes to remove your limits require a 12-hour cool off period. ')}
            position="top"
            contentClass="w-[15rem] !text-[.75rem]"
          >
            <IconCheckQuestion class="size-4 ml-[.12rem] cursor-pointer" />
          </Tooltip>
        </span>
      ),
      align: 'right',
      render: (r) => {
        const removable = canRemove(r.currentTime, r.createTime, r.limitHour);
        return (
          <button
            class={`font-semibold text-sm leading-[1.2] underline underline-offset-2 ${
              removable ? 'text-brand-default cursor-pointer' : 'text-neutral-main5 cursor-not-allowed'
            }`}
            disabled={!removable}
            onClick={(e) => {
              e.stopPropagation();
              removable && handleRemove(r);
            }}
          >
            {t('Remove')}
          </button>
        );
      },
    },
  ]);

  return (
    <div class="bg-layer4-card flex flex-col gap-4 p-4 rounded-xl shadow-[0px_2px_24px_0px_rgba(10,15,19,0.05)] w-full">
      <div class="bg-layer3-card border border-main2-auto flex flex-col gap-3 px-4 py-4 rounded-lg shadow-[0px_2px_16px_0px_rgba(10,15,19,0.05)] w-full">
        <div class="flex flex-col gap-2 w-full">
          <div class="font-semibold text-base text-neutral-main1 leading-[1.5]">{t('Gambling Limits')}</div>

          {/* Table — shown when limits exist */}
          <Show when={hasLimits()}>
            <div class="hidden lg:block ">
              <Table
                columns={columns as any}
                data={limits() as any}
                className="w-full overflow-x-auto rounded-lg border border-main3"
              />
            </div>
            <div class="block lg:hidden">
              <For each={limits()}>
                {(limitItem) => {
                  const removable = canRemove(limitItem.currentTime, limitItem.createTime, limitItem.limitHour);

                  return (
                    <div class="border border-main3 rounded-lg p-4 mb-3 w-full">
                      <div>
                        <div class="flex justify-between mb-4">
                          <div class="text-neutral-main3 text-sm font-semibold">{t('Type')}</div>
                          <div class="font-semibold text-sm text-neutral-main1 leading-[1.2]">
                            {typeLabel(limitItem.limitType)}
                          </div>
                        </div>
                        <div class="flex justify-between mb-4">
                          <div class="text-neutral-main3 text-sm font-semibold">{t('period')}</div>
                          <div class="font-semibold text-sm text-neutral-main1 leading-[1.2]">
                            {periodLabel(limitItem.dayType)}
                          </div>
                        </div>
                        <div class="flex justify-between mb-4">
                          <div class="text-neutral-main3 text-sm font-semibold">{t('Time Remaining')}</div>
                          <div class="font-semibold text-sm text-neutral-main1 leading-[1.2]">
                            {timeRemaining(limitItem.endTime, limitItem.currentTime)}
                          </div>
                        </div>
                        <div class="flex justify-between mb-4">
                          <div class="text-neutral-main3 text-sm font-semibold">{t('Limit')}</div>
                          <div class="font-semibold text-sm text-neutral-main1 leading-[1.2]">
                            ${limitItem.limitAmount.toFixed(2)}
                          </div>
                        </div>
                        <div class="flex justify-between mb-4">
                          <div class="flex items-center gap-1 text-neutral-main3 text-sm font-semibold">
                            {t('Progress')}
                            <Tooltip content={t('There may be a certain delay in betting settlement.')} position="top">
                              <IconCheckQuestion class="size-4 ml-[.12rem] cursor-pointer" />
                            </Tooltip>
                          </div>
                          <div class="font-semibold text-sm text-neutral-main1 leading-[1.2]">
                            ${limitItem.progressAmount.toFixed(2)}
                          </div>
                        </div>
                        <div class="flex justify-between mb-4">
                          <div class="flex items-center gap-1 text-neutral-main3 text-sm font-semibold">
                            {t('Action')}
                            <Tooltip
                              content={t('Any changes to remove your limits require a 12-hour cool off period. ')}
                              position="top"
                              contentClass="w-[15rem] !text-[.75rem]"
                            >
                              <IconCheckQuestion class="size-4 ml-[.12rem] cursor-pointer" />
                            </Tooltip>
                          </div>
                          <button
                            class={`font-semibold text-sm leading-[1.2] underline underline-offset-2 ${
                              removable ? 'text-brand-default cursor-pointer' : 'text-neutral-main5 cursor-not-allowed'
                            }`}
                            disabled={!removable}
                            onClick={(e) => {
                              e.stopPropagation();
                              removable && handleRemove(limitItem);
                            }}
                          >
                            {t('Remove')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }}
              </For>
            </div>
          </Show>

          <p class="font-semibold text-sm text-neutral-main3 leading-[1.5]">
            {t(
              'Gain control over your play or betting by using loss or wagering limits. These limits allow you to control the maximum loss or wagered amount over a daily, weekly or monthly period.'
            )}
          </p>
          <p class="font-semibold text-sm text-neutral-main3 leading-[1.5]">
            {t(
              'Your limit will apply immediately and will reset when that time is reached. E.g. If you set a $100 daily loss limit at 2pm today, the limit will reset at 2pm tomorrow.'
            )}
          </p>
          <p class="font-semibold text-sm text-neutral-main3 leading-[1.5]">
            {t('Any changes to remove your limits require a 12-hour cool off period. ')}
          </p>
        </div>

        <div class="flex items-center w-full">
          <A href="/responsible/limit">
            <Button type="brand" class="h-11 min-w-[200px] w-[200px] rounded-full" disabled={!canAddMore()}>
              {t('Set Gambling Limit')}
            </Button>
          </A>
        </div>
      </div>
    </div>
  );
}
